import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import addContact from "../../assets/images/add-contact.svg";
import chat from "../../assets/images/chat-two.svg";
import mobileBanking from "../../assets/images/mobile-banking.svg";
import calendar from "../../assets/images/calendar.svg";

import { useNavigate } from "react-router-dom";

// Import your hero image
import manWithDog from "../../assets/images/man-with-dog.png";

const BecomeASitter: React.FC = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: (
        <img src={addContact} alt="addContact" width="54px" height="54px" />
      ),
      title: "1. Create a Standout Profile:",
      description:
        "Craft an engaging profile that showcases your experience, skills, and genuine love for animals. Highlight your unique qualities and certifications to attract pet owners seeking reliable and compassionate sitters.",
    },
    {
      icon: <img src={chat} alt="chat" width="54px" height="54px" />,
      title: "2. Build Trust with Pet Owners:",
      description:
        "Nurture trust and credibility by collecting authentic reviews and ratings from satisfied pet owners. Provide exceptional care, establish open communication, and develop lasting relationships with both pets and their owners.",
    },
    {
      icon: (
        <img
          src={mobileBanking}
          alt="mobileBanking"
          width="54px"
          height="54px"
        />
      ),
      title: "3. Safe and Secure Transactions:",
      description:
        "Rest easy knowing that all transactions are secure, and pet owners undergo a verification process. We prioritize the safety and well-being of both pets and sitters, ensuring a reliable and trustworthy platform for all.",
    },
    {
      icon: <img src={calendar} alt="calendar" width="54px" height="54px" />,
      title: "4. Flexible Scheduling:",
      description:
        "Enjoy the freedom to set your own schedule and determine the number of clients you can accommodate. Whether you prefer part-time or full-time pet sitting, this platform allows you to find the balance that suits your lifestyle.",
    },
  ];

  return (
    <Box sx={{ backgroundColor: "#FAF8F5", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.8rem" },
                fontWeight: 700,
                color: "#178B8E",
                mb: 3,
              }}
            >
              Turn your love for animals into a rewarding career
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                color: "#5A5A5A",
                mb: 4,
                lineHeight: 1.8,
              }}
            >
              If you have a deep affection for animals and a desire to provide
              exceptional care, this is the platform for you. Join our vibrant
              community of pet sitters and unlock a world of opportunities to
              make a positive impact on the lives of pets and their owners.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/register")}
              sx={{
                backgroundColor: "#178B8E",
                fontSize: "1.1rem",
                px: 4,
                py: 1.5,
                "&:hover": {
                  backgroundColor: "#0F6164",
                },
              }}
            >
              Get Started
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={manWithDog}
              alt="Man with French Bulldog"
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: { xs: 400, md: "none" },
                objectFit: "cover",
                display: "block",
                borderRadius: 2,
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Steps Section */}
      <Box sx={{ backgroundColor: "#FFFFFF", py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    p: 3,
                    backgroundColor: "#FAF8F5",
                    boxShadow: "none",
                    border: "1px solid #E5E5E5",
                    "&:hover": {
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      transform: "translateY(-4px)",
                      transition: "all 0.3s ease",
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 2 }}>{step.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: "#178B8E",
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#5A5A5A",
                        lineHeight: 1.7,
                      }}
                    >
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                color: "#178B8E",
                fontWeight: 600,
                mb: 4,
                fontSize: { xs: "1.5rem", md: "2rem" },
              }}
            >
              Start connecting with pet owners as soon as your profile is
              approved.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/register")}
              sx={{
                backgroundColor: "#178B8E",
                fontSize: "1.1rem",
                px: 5,
                py: 1.5,
                "&:hover": {
                  backgroundColor: "#0F6164",
                },
              }}
            >
              Start Creating Your Profile
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default BecomeASitter;
